import { Head, router, useForm } from "@inertiajs/react"
import {
  Users,
  Search,
  Plus,
  Mail,
  Send,
  Download,
  UserX,
  UserCheck,
  Shield,
  VenetianMask,
  Copy,
  ExternalLink,
  AlertTriangle,
} from "lucide-react"
import { useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const userManagementTabs = [
  { label: "People", href: "/app/admin/people" },
  { label: "Groups", href: "/app/admin/groups" },
]
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

const roleConfig = {
  coach: { label: "Coach", className: "bg-kf-red text-white hover:bg-kf-red" },
  member: {
    label: "Member",
    className: "bg-blue-500 text-white hover:bg-blue-500",
  },
  guest: {
    label: "Guest",
    className: "bg-zinc-400 text-white hover:bg-zinc-400",
  },
  // "prospect" isn't a DB role — it's a guest without author access (a sales
  // prospect vs a content contributor). Resolved via effectiveRoleKey() so the
  // overloaded guest role reads correctly in the table.
  prospect: {
    label: "Prospect",
    className: "bg-teal-500 text-white hover:bg-teal-500",
  },
}

// Splits the overloaded guest role for display/filtering: a guest who can't
// author articles is a Prospect; one who can is a Guest (contributor).
const effectiveRoleKey = (person) =>
  person.role === "guest" && !person.isAuthor ? "prospect" : person.role

const statusConfig = {
  verified: {
    label: "Active",
    className: "bg-green-500 text-white hover:bg-green-500",
  },
  unverified: {
    label: "Unverified",
    className: "bg-amber-500 text-white hover:bg-amber-500",
  },
  closed: {
    label: "Deactivated",
    className: "bg-red-500 text-white hover:bg-red-500",
  },
}

const roleFilters = [
  { key: null, label: "All", statKey: "total" },
  { key: "coach", label: "Coaches", statKey: "coaches" },
  { key: "member", label: "Members", statKey: "members" },
  { key: "prospect", label: "Prospects", statKey: "prospects" },
  { key: "guest", label: "Guests", statKey: "guests" },
  { key: "admin", label: "Admins", statKey: "admins" },
]

// For describing the current server-side segment on the "Email all …" button.
const ROLE_PLURAL = { coach: "coaches", member: "members", prospect: "prospects", guest: "guests", admin: "admins" }
const STATUS_LABEL = { verified: "active", unverified: "unverified", closed: "deactivated" }

function PersonDialog({ open, onOpenChange, person = null }) {
  const isEditing = !!person
  const form = useForm({
    name: person?.name || "",
    email: person?.email || "",
    role: person?.role || "guest",
    is_admin: person?.isAdmin || false,
    is_beta_tester: person?.isBetaTester || false,
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isEditing) {
      form.patch(`/app/admin/people/${person.id}`, {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    } else {
      form.post("/app/admin/people", {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Person" : "Add Person"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update this person's details."
                : "Create a new account. You can send an invite email after."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="John Doe"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
              />
              {form.errors.name && (
                <p className="text-sm text-red-600">{form.errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.email}
                onChange={(e) => form.setData("email", e.target.value)}
                required
              />
              {form.errors.email && (
                <p className="text-sm text-red-600">{form.errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={form.data.role}
                onValueChange={(value) => form.setData("role", value)}
              >
                <SelectTrigger className="w-full bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="guest">Guest</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
              <div className="space-y-0.5">
                <Label htmlFor="is_admin" className="cursor-pointer">
                  Admin Access
                </Label>
                <p className="text-xs text-zinc-500">
                  Grant admin privileges to this person
                </p>
              </div>
              <Switch
                id="is_admin"
                checked={form.data.is_admin}
                onCheckedChange={(checked) => form.setData("is_admin", checked)}
              />
            </div>
            {form.errors.is_admin && (
              <p className="text-sm text-red-600">{form.errors.is_admin}</p>
            )}

            <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
              <div className="space-y-0.5">
                <Label htmlFor="is_beta_tester" className="cursor-pointer">
                  Beta Tester
                </Label>
                <p className="text-xs text-zinc-500">
                  Grants access to in-development features
                </p>
              </div>
              <Switch
                id="is_beta_tester"
                checked={form.data.is_beta_tester}
                onCheckedChange={(checked) => form.setData("is_beta_tester", checked)}
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEditing
                ? "Save Changes"
                : "Create Person"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function PeopleIndex({
  currentUser,
  people,
  stats,
  currentRole,
  currentStatus,
  currentCoachConnection,
  availableCoaches = [],
  pendingCount,
}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [coachFilter, setCoachFilter] = useState("all")
  const [createOpen, setCreateOpen] = useState(false)
  const [createKey, setCreateKey] = useState(0)
  const [deactivatePerson, setDeactivatePerson] = useState(null)
  const [deactivateError, setDeactivateError] = useState(null)
  const [reactivatePerson, setReactivatePerson] = useState(null)
  const [invitePerson, setInvitePerson] = useState(null)
  const [impersonateConfirm, setImpersonateConfirm] = useState(null)
  const [impersonateResult, setImpersonateResult] = useState(null)
  const [impersonating, setImpersonating] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const [selectedIds, setSelectedIds] = useState(() => new Set())

  const handleFilterChange = (role) => {
    const params = {}
    if (role) params.role = role
    if (currentStatus) params.status = currentStatus
    if (currentCoachConnection) params.coach_connection = currentCoachConnection
    router.get("/app/admin/people", params, { preserveState: true })
  }

  const handleStatusFilter = (value) => {
    const params = {}
    if (currentRole) params.role = currentRole
    if (value !== "all") params.status = value
    if (currentCoachConnection) params.coach_connection = currentCoachConnection
    router.get("/app/admin/people", params, { preserveState: true })
  }

  const handleCoachConnectionFilter = (value) => {
    const params = {}
    if (currentRole) params.role = currentRole
    if (currentStatus) params.status = currentStatus
    if (value !== "all") params.coach_connection = value
    router.get("/app/admin/people", params, { preserveState: true })
  }

  // Score a person against the search query so direct name hits sort
  // above indirect matches (group, coach). Returns score=0 for non-matches
  // and a short `reason` string for indirect matches so the row can show
  // "Coached by …" or "In group: …" context.
  const scorePerson = (person, query) => {
    if (!query) return { score: 1, reason: null }
    const name = (person.name || "").toLowerCase()
    const email = person.email.toLowerCase()

    if (name === query) return { score: 100, reason: null }
    if (name.startsWith(query)) return { score: 90, reason: null }
    if (name.includes(query)) return { score: 80, reason: null }
    if (email.includes(query)) return { score: 60, reason: null }

    const matchedGroup = (person.groupNames || []).find((g) => g.toLowerCase().includes(query))
    if (matchedGroup) return { score: 40, reason: `In group: ${matchedGroup}` }

    const matchedCoach = (person.coaches || []).find((c) => c.name.toLowerCase().includes(query))
    if (matchedCoach) return { score: 30, reason: `Coached by ${matchedCoach.name}` }

    return { score: 0, reason: null }
  }

  const query = searchQuery.trim().toLowerCase()

  const filteredPeople = (people || [])
    .filter((person) => {
      if (coachFilter === "all") return true
      return (person.coaches || []).some((c) => String(c.id) === coachFilter)
    })
    .map((person) => ({ person, ...scorePerson(person, query) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => ({ ...entry.person, _matchReason: entry.reason }))

  // --- Broadcast audience selection (U7) ---

  const visibleIds = filteredPeople.map((p) => p.id)
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id))
      else visibleIds.forEach((id) => next.add(id))
      return next
    })
  }

  // Human label for the current server-side segment (role + status filters).
  const segmentLabel = () => {
    if (!currentRole && !currentStatus) return "everybody"
    const parts = []
    if (currentStatus) parts.push(STATUS_LABEL[currentStatus] || currentStatus)
    parts.push(currentRole ? ROLE_PLURAL[currentRole] || currentRole : "people")
    return parts.join(" ")
  }

  // "Email all matching" → resolve the whole server-side segment in the composer
  // (not just the visible rows), so it reaches every match across the filter (AE4).
  const emailSegment = () => {
    const params =
      !currentRole && !currentStatus
        ? { audience_type: "everybody" }
        : { audience_type: "segment", ...(currentRole && { role: currentRole }), ...(currentStatus && { status: currentStatus }) }
    router.get("/app/admin/broadcasts/new", params)
  }

  // Hand-picked recipients → an explicit id list.
  const emailSelected = () => {
    router.get("/app/admin/broadcasts/new", {
      audience_type: "selection",
      account_ids: Array.from(selectedIds),
    })
  }

  // CSV download honors the current server-side role/status/coach-connection
  // filter. Not routed through Inertia — it's a file download (window.location),
  // not a page visit.
  const exportCsv = () => {
    const params = new URLSearchParams()
    if (currentRole) params.set("role", currentRole)
    if (currentStatus) params.set("status", currentStatus)
    if (currentCoachConnection) params.set("coach_connection", currentCoachConnection)
    const qs = params.toString()
    window.location.href = `/app/admin/people/export${qs ? `?${qs}` : ""}`
  }

  const handleDeactivate = () => {
    if (!deactivatePerson) return
    setDeactivateError(null)
    router.delete(`/app/admin/people/${deactivatePerson.id}`, {
      preserveScroll: true,
      // A genuine deactivation closes the dialog. A refusal (e.g. the coach
      // still leads a group) comes back as an Inertia validation error — keep
      // the dialog open and show the reason instead of silently closing.
      onSuccess: () => setDeactivatePerson(null),
      onError: (errors) =>
        setDeactivateError(errors.base || "Could not deactivate this account."),
    })
  }

  // Confirmed, not silent: deactivation moves a coach's articles to Kingdom
  // Factor automatically, so the repair states its count up front (#488). The
  // flash then reports what actually happened.
  const handleReactivate = () => {
    if (!reactivatePerson) return
    router.post(`/app/admin/people/${reactivatePerson.id}/reactivate`, {}, {
      preserveScroll: true,
      onFinish: () => setReactivatePerson(null),
    })
  }

  const handleSendInvite = () => {
    if (!invitePerson) return
    router.post(
      `/app/admin/people/${invitePerson.id}/send_invite`,
      {},
      {
        preserveScroll: true,
        onFinish: () => setInvitePerson(null),
      }
    )
  }

  const handleConfirmImpersonate = async () => {
    if (!impersonateConfirm) return
    setImpersonating(true)
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) {
        alert("Missing CSRF token — try refreshing the page.")
        return
      }
      const res = await fetch(`/app/admin/people/${impersonateConfirm.id}/impersonate`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
      })

      let data = null
      const contentType = res.headers.get("content-type") || ""
      if (contentType.includes("application/json")) {
        data = await res.json()
      }

      if (!res.ok) {
        const reason = data?.error || `${res.status} ${res.statusText}`
        alert(`Could not generate impersonation link: ${reason}`)
        return
      }
      setImpersonateConfirm(null)
      setLinkCopied(false)
      setImpersonateResult(data)
    } catch (err) {
      alert(`Network error generating link: ${err.message || err}`)
    } finally {
      setImpersonating(false)
    }
  }

  const copyImpersonateLink = async () => {
    if (!impersonateResult?.url) return
    try {
      await navigator.clipboard.writeText(impersonateResult.url)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2500)
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea")
      ta.value = impersonateResult.url
      document.body.appendChild(ta)
      ta.select()
      document.execCommand("copy")
      document.body.removeChild(ta)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2500)
    }
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="People - Kingdom Factor Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={userManagementTabs} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">People</h1>
            <p className="text-zinc-500">
              Manage coaches, members, and guests
            </p>
          </div>
          <Button variant="gradient" onClick={() => { setCreateKey(k => k + 1); setCreateOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Person
          </Button>
        </div>

        {/* Stats filter cards */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
          {roleFilters.map((filter) => {
            const count = stats?.[filter.statKey] || 0
            const isActive = currentRole === filter.key
            return (
              <button
                key={filter.label}
                onClick={() => handleFilterChange(filter.key)}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  isActive
                    ? "border-kf-red bg-red-50"
                    : "border-zinc-200 hover:border-zinc-300 bg-white"
                }`}
              >
                <p className="text-sm text-zinc-500">{filter.label}</p>
                <p className="text-2xl font-bold text-zinc-900">{count}</p>
              </button>
            )
          })}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              type="search"
              placeholder="Search by name, email, group, or coach..."
              className="pl-9 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={coachFilter} onValueChange={setCoachFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Coaches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Coaches</SelectItem>
              {availableCoaches.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={currentStatus || "all"} onValueChange={handleStatusFilter}>
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="verified">Active</SelectItem>
              <SelectItem value="closed">Deactivated</SelectItem>
              <SelectItem value="unverified">Unverified</SelectItem>
            </SelectContent>
          </Select>
          <Select value={currentCoachConnection || "all"} onValueChange={handleCoachConnectionFilter}>
            <SelectTrigger className="w-full sm:w-[190px]">
              <SelectValue placeholder="Coach Connection" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Coach Connection</SelectItem>
              <SelectItem value="none">No coach assigned</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bulk actions: email the audience + export */}
        <div className="flex flex-wrap items-center gap-2">
          {selectedIds.size > 0 ? (
            <>
              <Button variant="gradient" onClick={emailSelected}>
                <Send className="h-4 w-4 mr-2" />
                Email {selectedIds.size} selected
              </Button>
              <Button variant="ghost" onClick={() => setSelectedIds(new Set())}>
                Clear selection
              </Button>
            </>
          ) : (
            <Button variant="kf-outline" onClick={emailSegment}>
              <Send className="h-4 w-4 mr-2" />
              Email all {segmentLabel()}
            </Button>
          )}
          <Button variant="outline" onClick={exportCsv}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* People Table */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-0">
            {filteredPeople?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all visible rows"
                        title="Select all visible rows. To reach every match across the whole filter, clear the selection and use “Email all …”."
                        className="h-4 w-4 rounded border-zinc-300 text-kf-red focus:ring-kf-red cursor-pointer align-middle"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                      />
                    </TableHead>
                    <TableHead className="text-zinc-500">Name</TableHead>
                    <TableHead className="text-zinc-500 hidden md:table-cell">Email</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">Role</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">Status</TableHead>
                    <TableHead className="text-zinc-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPeople.map((person) => {
                    const role = roleConfig[effectiveRoleKey(person)] || roleConfig.guest
                    const status =
                      statusConfig[person.status] || statusConfig.unverified
                    return (
                      <TableRow
                        key={person.id}
                        className="border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                        onClick={() =>
                          router.visit(`/app/admin/people/${person.id}`)
                        }
                      >
                        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            aria-label={`Select ${person.name || person.email}`}
                            className="h-4 w-4 rounded border-zinc-300 text-kf-red focus:ring-kf-red cursor-pointer align-middle"
                            checked={selectedIds.has(person.id)}
                            onChange={() => toggleSelect(person.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-zinc-900 whitespace-normal">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {person.name || (
                                <span className="text-zinc-400 italic">
                                  No name
                                </span>
                              )}
                              {person.isAdmin && (
                                <Badge
                                  variant="outline"
                                  className="border-amber-500 text-amber-600 text-xs"
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  Admin
                                </Badge>
                              )}
                              {person.isBetaTester && !person.isAdmin && (
                                <Badge
                                  variant="outline"
                                  className="border-kf-red text-kf-red text-xs"
                                >
                                  Beta
                                </Badge>
                              )}
                            </div>
                            {person._matchReason && (
                              <p className="text-xs text-zinc-400 mt-0.5 truncate">
                                {person._matchReason}
                              </p>
                            )}
                            <p className="text-sm text-zinc-500 truncate md:hidden">
                              {person.email}
                            </p>
                            <div className="mt-1.5 flex items-center gap-1.5 sm:hidden">
                              <Badge className={role.className}>{role.label}</Badge>
                              <Badge className={status.className}>
                                {status.label}
                              </Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-zinc-500 hidden md:table-cell">
                          {person.email}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge className={role.className}>{role.label}</Badge>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge className={status.className}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {person.status === "verified" && (
                              <>
                                {person.id !== currentUser.id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setImpersonateConfirm(person)
                                    }}
                                    title="Impersonate"
                                    className="text-zinc-500 hover:text-amber-600 hover:bg-amber-50"
                                  >
                                    <VenetianMask className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setInvitePerson(person)
                                  }}
                                  title={
                                    person.invitedAt
                                      ? "Resend Invite"
                                      : "Send Invite"
                                  }
                                >
                                  <Mail className="h-4 w-4" />
                                </Button>
                                {person.id !== currentUser.id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setDeactivateError(null)
                                      setDeactivatePerson(person)
                                    }}
                                    title="Deactivate"
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <UserX className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                            {person.status === "closed" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setReactivatePerson(person)
                                }}
                                title="Reactivate"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <Users className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-500">No people found</p>
                <p className="text-sm">
                  {searchQuery
                    ? "Try a different search term"
                    : "Add your first person to get started"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Person Dialog */}
      <PersonDialog key={createKey} open={createOpen} onOpenChange={setCreateOpen} />

      {/* Deactivate Confirmation */}
      <AlertDialog
        open={!!deactivatePerson}
        onOpenChange={(open) => {
          if (!open) {
            setDeactivatePerson(null)
            setDeactivateError(null)
          }
        }}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate{" "}
              <strong>
                {deactivatePerson?.name || deactivatePerson?.email}
              </strong>
              ? They will no longer be able to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deactivateError && (
            <p
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {deactivateError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              // preventDefault stops Radix from auto-closing the dialog; the
              // router.delete callbacks decide whether to close (success) or
              // keep it open with an error (refusal).
              onClick={(e) => {
                e.preventDefault()
                handleDeactivate()
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate Confirmation */}
      <AlertDialog
        open={!!reactivatePerson}
        onOpenChange={(open) => !open && setReactivatePerson(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reactivate{" "}
              <strong>
                {reactivatePerson?.name || reactivatePerson?.email}
              </strong>
              ? They will be able to log in again.
              {reactivatePerson?.restorableArticleCount > 0 && (
                <>
                  {" "}
                  {reactivatePerson.restorableArticleCount}{" "}
                  {reactivatePerson.restorableArticleCount === 1
                    ? "article"
                    : "articles"}{" "}
                  will return to{" "}
                  {reactivatePerson.name || reactivatePerson.email}.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReactivate}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Invite Confirmation */}
      <AlertDialog
        open={!!invitePerson}
        onOpenChange={(open) => !open && setInvitePerson(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {invitePerson?.invitedAt ? "Resend invite email?" : "Send invite email?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will email an invite link to{" "}
              <strong>
                {invitePerson?.name
                  ? `${invitePerson.name} (${invitePerson.email})`
                  : invitePerson?.email}
              </strong>
              .
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendInvite}
              className="bg-kf-red hover:bg-red-700 text-white"
            >
              {invitePerson?.invitedAt ? "Resend invite" : "Send invite"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Impersonate confirmation */}
      <AlertDialog
        open={!!impersonateConfirm}
        onOpenChange={(open) => !open && setImpersonateConfirm(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Impersonate {impersonateConfirm?.name || impersonateConfirm?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <span className="block">
                This generates a one-time login link valid for 15 minutes. Open it in an
                <strong> incognito / private window</strong> so it doesn&rsquo;t replace your admin session.
              </span>
              <span className="block rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900 text-sm">
                Anything you do in that session affects this user&rsquo;s real account &mdash; messages, edits, deletes are all live.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={impersonating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleConfirmImpersonate()
              }}
              disabled={impersonating}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {impersonating ? "Generating..." : "Generate link"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Impersonate result — link + copy */}
      <ResponsiveDialog
        open={!!impersonateResult}
        onOpenChange={(open) => { if (!open) { setImpersonateResult(null); setLinkCopied(false) } }}
      >
        <ResponsiveDialogContent className="sm:max-w-lg">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              Impersonation link for {impersonateResult?.target?.name || impersonateResult?.target?.email}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Paste this link into an incognito / private window to keep both sessions side by side. It expires in 15 minutes.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-3 py-4 px-4 sm:px-0">
            <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 break-all font-mono text-xs text-zinc-700">
              {impersonateResult?.url}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="gradient"
                onClick={copyImpersonateLink}
                className="flex-1"
              >
                <Copy className="h-4 w-4 mr-2" />
                {linkCopied ? "Copied!" : "Copy link"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(impersonateResult?.url, "_blank", "noopener")}
                className="flex-1"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in new tab
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              Opening it in this browser signs you in as them here. A "Return to admin"
              button follows you onto every page, including the report view, so you can
              always get back.
            </p>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">Done</Button>
            </ResponsiveDialogClose>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </AdminLayout>
  )
}
